<template>
    <v-sheet
        :min-width="minWidth"
        :elevation="elevation"
    >
        <form
            class="pa-1"
            role="search"
            @submit="onSubmit($event)"
        >
            <v-text-field
                v-model="searchTerm"
                type="text"
                aria-label="Search"
                :placeholder="placeholder"
                :prepend-inner-icon="prependIcon ? mdiMagnify : undefined"
                variant="outlined"
                hide-details="auto"
                :density="density"
                :autofocus="autofocus"
                @click:clear="clearMessage"
                clearable
                indent-details
            >
                <template #append-inner>
                    <v-btn
                        class="px-0"
                        type="submit"
                        color="secondary"
                        variant="plain"
                        :height="density === 'compact' ? 32 : 40"
                        @click.stop
                    >
                        Search
                    </v-btn>
                </template>
            </v-text-field>
        </form>
    </v-sheet>
</template>

<script lang="ts" setup>
    import { mdiMagnify } from '@mdi/js';

    const props = withDefaults(
        defineProps<{
            elevation?: number;
            minWidth?: number;
            placeholder?: string;
            density?: 'default' | 'comfortable' | 'compact';
            // Show a magnifier icon inside the field.
            prependIcon?: boolean;
            autofocus?: boolean;
            // Empty the field after submitting. Off when the field should keep
            // showing the term that was searched, as on the search page.
            clearOnSubmit?: boolean;
            // Emit `on-close` before any route change, for use inside a menu.
            closeOnNavigate?: boolean;
        }>(),
        {
            density: 'compact',
            autofocus: true,
            clearOnSubmit: true,
            closeOnNavigate: true
        }
    );

    const emit = defineEmits<{
        (e: 'on-close'): void;
        (e: 'on-submit', q: string): void;
    }>();

    // The field's text. Optional: the parent can pass `v-model` to prefill or
    // sync it (as the search page does with the `q` query param).
    const searchTerm = defineModel<string>({ default: '' });

    const router = useRouter();

    function clearMessage() {
        searchTerm.value = '';
    }

    async function onSubmit(event: Event) {
        event.preventDefault();
        const q = searchTerm.value;

        if (props.clearOnSubmit) {
            clearMessage();
        }

        emit('on-submit', q);
        emit('on-close');
        await navigateTo({ path: '/search', query: { q } });
    }

    function onClose() {
        emit('on-close');
    }

    const unBeforeHook = props.closeOnNavigate
        ? router.beforeEach(() => {
            onClose();
        })
        : undefined;

    onBeforeUnmount(() => {
        unBeforeHook?.();
    });
</script>

<style lang="scss" scoped></style>
